import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Plus, Minus, Search, Calendar, User, Phone, CreditCard, Banknote, Trash2, ChevronLeft, ChevronRight, Check, X, FileText, Wallet, ArrowDownCircle, BarChart3, Printer, TrendingUp, TrendingDown } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { calculateCateringTotal, getCateringBreakdown, BASE_PRICE } from '../../utils/pricing';
import VeresiyeNewEntryDialog from './VeresiyeNewEntryDialog';
import VeresiyeCustomerStatement from './VeresiyeCustomerStatement';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VeresiyeManagement = ({ token }) => {
  const [customers, setCustomers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [view, setView] = useState('customers');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showNewEntry, setShowNewEntry] = useState(false);
  const [showStatement, setShowStatement] = useState(false);
  const [showPayment, setShowPayment] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [beverageItems, setBeverageItems] = useState([]);
  const [cateringMenu, setCateringMenu] = useState(null);

  // New entry form
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [cart, setCart] = useState([]);
  const [menuSearch, setMenuSearch] = useState('');
  const [menuTab, setMenuTab] = useState('alakart');
  const [discountPct, setDiscountPct] = useState('0');

  // Payment form
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('nakit');
  const [payNotes, setPayNotes] = useState('');

  // Bulk payment
  const [selectedEntryIds, setSelectedEntryIds] = useState([]);
  const [showBulkPayment, setShowBulkPayment] = useState(false);
  const [bulkPayAmount, setBulkPayAmount] = useState('');
  const [bulkPayMethod, setBulkPayMethod] = useState('nakit');
  const [bulkPayNotes, setBulkPayNotes] = useState('');

  // Report state
  const [report, setReport] = useState(null);
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/veresiye/customers`, { headers });
      setCustomers(res.data);
    } catch { toast.error('Musteri listesi alinamadi'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => {
    fetchCustomers();
    axios.get(`${API}/menu`).then(r => setMenuItems(r.data)).catch(() => {});
    axios.get(`${API}/beverages`).then(r => setBeverageItems(r.data)).catch(() => {});
    axios.get(`${API}/catering/today`).then(r => { if (r.data) setCateringMenu(r.data); }).catch(() => {});
  }, [fetchCustomers]);

  const fetchCustomerEntries = async (name, phone) => {
    setLoading(true);
    try {
      const params = { customer_name: name };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${API}/admin/veresiye`, { headers, params });
      setEntries(res.data.filter(e => e.customer_name === name && (e.customer_phone || '') === (phone || '')));
    } catch { toast.error('Veresiye kayitlari alinamadi'); }
    finally { setLoading(false); }
  };

  const openCustomerDetail = (cust) => {
    setSelectedCustomer(cust);
    setView('detail');
    fetchCustomerEntries(cust.customer_name, cust.customer_phone);
  };

  const goBack = () => {
    setView('customers');
    setSelectedCustomer(null);
    setEntries([]);
    setDateFrom('');
    setDateTo('');
    setSelectedEntryIds([]);
    fetchCustomers();
  };

  // Cart helpers
  const addToCart = (item) => {
    setCart(prev => {
      const key = `${item.name}-${item.item_type}`;
      const existing = prev.find(c => `${c.name}-${c.item_type}` === key);
      if (existing) return prev.map(c => `${c.name}-${c.item_type}` === key ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { 
        name: item.name, price: item.price, quantity: 1, 
        item_type: item.item_type || 'alakart',
        catering_id: item.id || null,
        catering_category: item.category || null
      }];
    });
  };

  const updateCartQty = (key, delta) => {
    setCart(prev => {
      return prev.map(c => {
        if (`${c.name}-${c.item_type}` === key) {
          const newQty = c.quantity + delta;
          return newQty <= 0 ? null : { ...c, quantity: newQty };
        }
        return c;
      }).filter(Boolean);
    });
  };

  const removeFromCart = (key) => {
    setCart(prev => prev.filter(c => `${c.name}-${c.item_type}` !== key));
  };

  // Cart total with catering algorithm applied
  const cartTotal = useMemo(() => {
    const nonCatering = cart.filter(c => c.item_type !== 'catering');
    const cateringCartItems = cart.filter(c => c.item_type === 'catering');
    
    // Non-catering: simple sum
    let total = nonCatering.reduce((s, c) => s + c.price * c.quantity, 0);
    
    // Catering: apply set algorithm
    if (cateringCartItems.length > 0 && cateringMenu) {
      // Build itemQuantities from cart catering items
      const itemQty = {};
      for (const ci of cateringCartItems) {
        if (ci.catering_id) {
          itemQty[ci.catering_id] = ci.quantity;
        }
      }
      total += calculateCateringTotal(cateringMenu, itemQty);
    }
    
    return total;
  }, [cart, cateringMenu]);

  // Discount + final total derived from cartTotal (subtotal)
  const discountValue = useMemo(() => {
    const pct = Math.max(0, Math.min(100, parseFloat(discountPct) || 0));
    return +(cartTotal * pct / 100).toFixed(2);
  }, [cartTotal, discountPct]);
  const finalTotal = useMemo(() => +(cartTotal - discountValue).toFixed(2), [cartTotal, discountValue]);

  // Submit new veresiye
  const handleCreateEntry = async () => {
    if (!newName.trim()) return toast.error('Musteri adi gerekli');
    if (cart.length === 0) return toast.error('En az 1 urun ekleyin');
    setLoading(true);
    try {
      const pct = Math.max(0, Math.min(100, parseFloat(discountPct) || 0));
      await axios.post(`${API}/admin/veresiye`, {
        customer_name: newName.trim(),
        customer_phone: newPhone.trim(),
        items: cart,
        notes: newNotes || null,
        subtotal_override: +cartTotal.toFixed(2),
        discount_percent: pct
      }, { headers });
      toast.success(`Veresiye kaydedildi: ${newName.trim()} - €${finalTotal.toFixed(2)}`);
      setShowNewEntry(false);
      setNewName('');
      setNewPhone('');
      setNewNotes('');
      setCart([]);
      setDiscountPct('0');
      fetchCustomers();
      if (selectedCustomer) fetchCustomerEntries(selectedCustomer.customer_name, selectedCustomer.customer_phone);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kayit basarisiz');
    } finally { setLoading(false); }
  };

  // Submit payment
  const handlePayment = async () => {
    const amount = parseFloat(payAmount);
    if (!amount || amount <= 0) return toast.error('Gecerli bir tutar girin');
    setLoading(true);
    try {
      await axios.post(`${API}/admin/veresiye/${showPayment.id}/payment`, {
        amount, payment_method: payMethod, notes: payNotes || null
      }, { headers });
      toast.success(`€${amount.toFixed(2)} odeme kaydedildi`);
      setShowPayment(null);
      setPayAmount('');
      setPayNotes('');
      fetchCustomers();
      if (selectedCustomer) fetchCustomerEntries(selectedCustomer.customer_name, selectedCustomer.customer_phone);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Odeme kaydedilemedi');
    } finally { setLoading(false); }
  };

  // Submit bulk payment
  const handleBulkPayment = async () => {
    if (selectedEntryIds.length === 0) return toast.error('En az 1 açık kayıt seçin');
    const amount = parseFloat(bulkPayAmount);
    if (!amount || amount <= 0) return toast.error('Geçerli bir tutar girin');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/veresiye/bulk-payment`, {
        entry_ids: selectedEntryIds,
        amount,
        payment_method: bulkPayMethod,
        notes: bulkPayNotes || null
      }, { headers });
      toast.success(`Toplu ödeme: €${(res.data?.applied ?? amount).toFixed(2)} — ${res.data?.closed_entries || 0} kayıt kapatıldı`);
      setShowBulkPayment(false);
      setBulkPayAmount('');
      setBulkPayNotes('');
      setBulkPayMethod('nakit');
      setSelectedEntryIds([]);
      fetchCustomers();
      if (selectedCustomer) fetchCustomerEntries(selectedCustomer.customer_name, selectedCustomer.customer_phone);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toplu ödeme kaydedilemedi');
    } finally { setLoading(false); }
  };

  const toggleEntrySelection = (entryId) => {
    setSelectedEntryIds(prev =>
      prev.includes(entryId) ? prev.filter(id => id !== entryId) : [...prev, entryId]
    );
  };

  // Compute remaining total of selected open entries
  const selectedRemainingTotal = useMemo(() => {
    return entries
      .filter(e => selectedEntryIds.includes(e.id) && e.status === 'open')
      .reduce((s, e) => s + Math.max(0, (e.total_amount || 0) - (e.paid_amount || 0)), 0);
  }, [entries, selectedEntryIds]);

  const openEntries = useMemo(() => entries.filter(e => e.status === 'open'), [entries]);

  const openBulkPaymentDialog = () => {
    if (selectedEntryIds.length === 0) return toast.error('Önce ödenecek kayıtları seçin');
    setBulkPayAmount(selectedRemainingTotal.toFixed(2));
    setBulkPayMethod('nakit');
    setBulkPayNotes('');
    setShowBulkPayment(true);
  };

  const selectAllOpenEntries = () => {
    setSelectedEntryIds(openEntries.map(e => e.id));
  };

  const clearSelection = () => setSelectedEntryIds([]);

  // Delete entry
  const handleDelete = async (entryId) => {
    if (!confirm('Bu veresiye kaydini silmek istediginizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/veresiye/${entryId}`, { headers });
      toast.success('Veresiye kaydı silindi');
      fetchCustomers();
      if (selectedCustomer) fetchCustomerEntries(selectedCustomer.customer_name, selectedCustomer.customer_phone);
    } catch { toast.error('Silinemedi'); }
  };

  // Fetch report
  const fetchReport = async () => {
    setReportLoading(true);
    try {
      const params = {};
      if (reportDateFrom) params.date_from = reportDateFrom;
      if (reportDateTo) params.date_to = reportDateTo;
      const res = await axios.get(`${API}/admin/veresiye/report`, { headers, params });
      setReport(res.data);
    } catch { toast.error('Rapor alinamadi'); }
    finally { setReportLoading(false); }
  };

  const openReport = () => {
    setView('report');
    fetchReport();
  };

  // Print report
  const printReport = () => {
    if (!report) return;
    const s = report.summary;
    const ps = report.payment_summary;
    const dateRange = (reportDateFrom || reportDateTo)
      ? `${reportDateFrom || '...'} - ${reportDateTo || '...'}`
      : 'Tum Zamanlar';
    const now = new Date().toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    const customerRows = (report.customers || []).map((c, i) =>
      `<tr><td>${i + 1}</td><td>${c.customer_name}</td><td>${c.customer_phone || '-'}</td><td>${c.entry_count}</td><td>${c.open_count}</td><td class="r">\u20AC${c.total_amount.toFixed(2)}</td><td class="r g">\u20AC${c.paid_amount.toFixed(2)}</td><td class="r ${c.balance > 0 ? 'rd' : 'g'}">\u20AC${c.balance.toFixed(2)}</td></tr>`
    ).join('');

    const paymentRows = (report.recent_payments || []).slice(0, 20).map(p =>
      `<tr><td>${new Date(p.created_at).toLocaleDateString('tr-TR')}</td><td>${p.customer_name}</td><td class="r g">\u20AC${p.amount.toFixed(2)}</td><td>${p.payment_method === 'nakit' ? 'Nakit' : 'Kart'}</td><td>${p.notes || '-'}</td></tr>`
    ).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Veresiye Raporu</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;padding:20px;font-size:12px;color:#000;max-width:900px;margin:0 auto}
h1{font-size:20px;text-align:center;margin-bottom:4px}h2{font-size:14px;margin:16px 0 8px;padding-bottom:4px;border-bottom:2px solid #000}
.sub{text-align:center;color:#666;margin-bottom:16px;font-size:11px}
.cards{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:16px}
.card{border:1px solid #ddd;padding:10px;border-radius:6px;text-align:center}
.card .v{font-size:18px;font-weight:900;margin-top:4px}.card .l{font-size:10px;color:#666}
.rd{color:#dc2626}.g{color:#16a34a}
table{width:100%;border-collapse:collapse;font-size:11px;margin-bottom:12px}
th,td{border:1px solid #ddd;padding:6px 8px;text-align:left}
th{background:#f5f5f5;font-weight:800;font-size:10px;text-transform:uppercase}
.r{text-align:right}
.ft{text-align:center;margin-top:16px;padding-top:8px;border-top:1px dashed #999;font-size:10px;color:#666}
@media print{body{padding:10px}@page{margin:10mm}}</style></head>
<body>
<h1>VERESIYE RAPORU</h1>
<p class="sub">Central Restaurant | ${dateRange} | ${now}</p>
<div class="cards">
<div class="card"><div class="l">Toplam Borc</div><div class="v">\u20AC${s.total_debt.toFixed(2)}</div></div>
<div class="card"><div class="l">Tahsilat</div><div class="v g">\u20AC${s.total_paid.toFixed(2)}</div></div>
<div class="card"><div class="l">Kalan Bakiye</div><div class="v rd">\u20AC${s.total_balance.toFixed(2)}</div></div>
<div class="card"><div class="l">Kayit (Acik/Kapali)</div><div class="v">${s.open_entries}/${s.closed_entries}</div></div>
</div>
<h2>MUSTERI BAZLI OZET</h2>
<table><thead><tr><th>#</th><th>Musteri</th><th>Telefon</th><th>Kayit</th><th>Acik</th><th class="r">Toplam</th><th class="r">Odenen</th><th class="r">Kalan</th></tr></thead>
<tbody>${customerRows}</tbody>
<tfoot><tr style="font-weight:900"><td colspan="5">GENEL TOPLAM</td><td class="r">\u20AC${s.total_debt.toFixed(2)}</td><td class="r g">\u20AC${s.total_paid.toFixed(2)}</td><td class="r rd">\u20AC${s.total_balance.toFixed(2)}</td></tr></tfoot>
</table>
${paymentRows ? `<h2>SON TAHSILATLAR</h2>
<table><thead><tr><th>Tarih</th><th>Musteri</th><th class="r">Tutar</th><th>Yontem</th><th>Not</th></tr></thead>
<tbody>${paymentRows}</tbody>
<tfoot><tr style="font-weight:900"><td colspan="2">TOPLAM TAHSILAT</td><td class="r g">\u20AC${ps.total_collected.toFixed(2)}</td><td colspan="2">Nakit: \u20AC${ps.cash_total.toFixed(2)} | Kart: \u20AC${ps.card_total.toFixed(2)}</td></tr></tfoot>
</table>` : ''}
<div class="ft">Central Hotel & Restaurant | Veresiye Raporu</div>
</body></html>`;

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    const tryPrint = () => {
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {}
      setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) {} }, 3000);
    };
    if (iframe.contentWindow.document.readyState === 'complete') setTimeout(tryPrint, 300);
    else iframe.onload = () => setTimeout(tryPrint, 300);
  };

  // Filtered menu items
  const filteredMenu = menuItems.filter(m => !menuSearch || m.name.toLowerCase().includes(menuSearch.toLowerCase()));
  const menuCategories = [...new Set(filteredMenu.map(m => m.category))];
  const filteredBev = beverageItems.filter(b => !menuSearch || b.name.toLowerCase().includes(menuSearch.toLowerCase()));
  const bevCategories = [...new Set(filteredBev.map(b => b.category))];

  // Catering items flat list
  const cateringItems = useMemo(() => {
    if (!cateringMenu) return [];
    const items = [];
    const cats = { corbalar: 'Corba', ana_yemekler: 'Ana Yemek', yardimci_yemekler: 'Yardimci', salatalar: 'Salata', mezeler: 'Meze' };
    for (const [key, label] of Object.entries(cats)) {
      for (const item of (cateringMenu[key] || [])) {
        items.push({ ...item, category: label, price: item.price_addition || 0, item_type: 'catering' });
      }
    }
    return items.filter(i => !menuSearch || i.name.toLowerCase().includes(menuSearch.toLowerCase()));
  }, [cateringMenu, menuSearch]);

  const filteredCustomers = customers.filter(c =>
    !search || c.customer_name.toLowerCase().includes(search.toLowerCase()) || (c.customer_phone || '').includes(search)
  );

  const totalBalance = customers.reduce((s, c) => s + (c.balance || 0), 0);

  // ========== REPORT VIEW ==========
  if (view === 'report') {
    const s = report?.summary || {};
    const ps = report?.payment_summary || {};
    const rCustomers = report?.customers || [];
    const rPayments = report?.recent_payments || [];

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => { setView('customers'); setReport(null); }} className="p-2 rounded-full bg-stone-100 hover:bg-stone-200 transition-colors" data-testid="report-back-btn">
              <ChevronLeft className="w-5 h-5 text-charcoal" />
            </button>
            <div>
              <h2 className="font-playfair text-xl font-semibold text-charcoal" data-testid="report-title">Veresiye Raporu</h2>
              <p className="text-sm text-charcoal/60">Ozet veresiye raporu</p>
            </div>
          </div>
          <Button onClick={printReport} disabled={!report} className="bg-charcoal text-white hover:bg-charcoal/90 rounded-full" data-testid="report-print-btn">
            <Printer className="w-4 h-4 mr-1" />Yazdir
          </Button>
        </div>

        {/* Date Filter */}
        <div className="flex flex-wrap items-center gap-2 bg-white rounded-xl p-3 border border-stone-200">
          <Calendar className="w-4 h-4 text-charcoal/40" />
          <Input type="date" value={reportDateFrom} onChange={e => setReportDateFrom(e.target.value)} className="w-40 text-sm" data-testid="report-date-from" />
          <span className="text-charcoal/40 text-sm">-</span>
          <Input type="date" value={reportDateTo} onChange={e => setReportDateTo(e.target.value)} className="w-40 text-sm" data-testid="report-date-to" />
          <Button size="sm" onClick={fetchReport} className="bg-amber-700 text-white hover:bg-amber-600 text-xs rounded-full" data-testid="report-filter-btn">Filtrele</Button>
          {(reportDateFrom || reportDateTo) && (
            <Button size="sm" variant="ghost" onClick={() => { setReportDateFrom(''); setReportDateTo(''); setTimeout(fetchReport, 0); }} className="text-xs text-charcoal/50">Temizle</Button>
          )}
        </div>

        {reportLoading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-amber-600 border-t-transparent mx-auto" /></div>
        ) : !report ? (
          <div className="text-center py-12 text-charcoal/40">Rapor yuklenemedi</div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white rounded-xl p-4 border border-stone-200 text-center">
                <div className="w-8 h-8 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-2">
                  <TrendingUp className="w-4 h-4 text-red-500" />
                </div>
                <p className="text-xs text-charcoal/50">Toplam Borc</p>
                <p className="font-bold text-xl text-charcoal" data-testid="report-total-debt">€{(s.total_debt || 0).toFixed(2)}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-stone-200 text-center">
                <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-2">
                  <TrendingDown className="w-4 h-4 text-green-500" />
                </div>
                <p className="text-xs text-charcoal/50">Toplam Tahsilat</p>
                <p className="font-bold text-xl text-green-600" data-testid="report-total-paid">€{(s.total_paid || 0).toFixed(2)}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-red-200 text-center">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Wallet className="w-4 h-4 text-red-600" />
                </div>
                <p className="text-xs text-charcoal/50">Kalan Bakiye</p>
                <p className="font-bold text-xl text-red-600" data-testid="report-total-balance">€{(s.total_balance || 0).toFixed(2)}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-stone-200 text-center">
                <div className="w-8 h-8 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-2">
                  <FileText className="w-4 h-4 text-amber-600" />
                </div>
                <p className="text-xs text-charcoal/50">Kayit Sayisi</p>
                <p className="font-bold text-xl text-charcoal" data-testid="report-entry-count">{s.total_entries || 0}</p>
                <p className="text-xs text-charcoal/40 mt-0.5">{s.open_entries || 0} acik / {s.closed_entries || 0} kapali</p>
              </div>
            </div>

            {/* Payment Method Breakdown */}
            {(ps.payment_count || 0) > 0 && (
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-50 rounded-xl p-3 border border-green-200 text-center">
                  <p className="text-xs text-green-700/70">Tahsilat Sayisi</p>
                  <p className="font-bold text-green-700" data-testid="report-payment-count">{ps.payment_count}</p>
                </div>
                <div className="bg-green-50 rounded-xl p-3 border border-green-200 text-center">
                  <p className="text-xs text-green-700/70 flex items-center justify-center gap-1"><Banknote className="w-3 h-3" />Nakit</p>
                  <p className="font-bold text-green-700" data-testid="report-cash-total">€{(ps.cash_total || 0).toFixed(2)}</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-3 border border-blue-200 text-center">
                  <p className="text-xs text-blue-700/70 flex items-center justify-center gap-1"><CreditCard className="w-3 h-3" />Kart</p>
                  <p className="font-bold text-blue-700" data-testid="report-card-total">€{(ps.card_total || 0).toFixed(2)}</p>
                </div>
              </div>
            )}

            {/* Customer Table */}
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
              <div className="px-4 py-3 bg-stone-50 border-b border-stone-200">
                <h3 className="font-semibold text-charcoal text-sm flex items-center gap-2">
                  <User className="w-4 h-4 text-amber-700" />Musteri Bazli Ozet ({rCustomers.length} musteri)
                </h3>
              </div>
              {rCustomers.length === 0 ? (
                <div className="text-center py-8 text-charcoal/40 text-sm">Kayit bulunamadi</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="report-customer-table">
                    <thead>
                      <tr className="border-b border-stone-200 bg-stone-50/50">
                        <th className="px-4 py-2 text-left text-xs font-semibold text-charcoal/60 uppercase">#</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-charcoal/60 uppercase">Musteri</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-charcoal/60 uppercase">Telefon</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-charcoal/60 uppercase">Kayit</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-charcoal/60 uppercase">Acik</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-charcoal/60 uppercase">Toplam</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-charcoal/60 uppercase">Odenen</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-charcoal/60 uppercase">Kalan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rCustomers.map((c, i) => (
                        <tr key={`report-${c.customer_name}-${c.customer_phone || ''}`} className="border-b border-stone-100 hover:bg-amber-50/50 transition-colors" data-testid={`report-row-${i}`}>
                          <td className="px-4 py-2.5 text-charcoal/50">{i + 1}</td>
                          <td className="px-4 py-2.5 font-medium text-charcoal">{c.customer_name}</td>
                          <td className="px-4 py-2.5 text-charcoal/60">{c.customer_phone || '-'}</td>
                          <td className="px-4 py-2.5 text-center">{c.entry_count}</td>
                          <td className="px-4 py-2.5 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.open_count > 0 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                              {c.open_count}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right font-medium">€{c.total_amount.toFixed(2)}</td>
                          <td className="px-4 py-2.5 text-right text-green-600">€{c.paid_amount.toFixed(2)}</td>
                          <td className="px-4 py-2.5 text-right font-bold">
                            <span className={c.balance > 0 ? 'text-red-600' : 'text-green-600'}>€{c.balance.toFixed(2)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-stone-50 font-bold border-t-2 border-stone-300">
                        <td colSpan={5} className="px-4 py-2.5 text-charcoal">GENEL TOPLAM</td>
                        <td className="px-4 py-2.5 text-right">€{(s.total_debt || 0).toFixed(2)}</td>
                        <td className="px-4 py-2.5 text-right text-green-600">€{(s.total_paid || 0).toFixed(2)}</td>
                        <td className="px-4 py-2.5 text-right text-red-600">€{(s.total_balance || 0).toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* Recent Payments */}
            {rPayments.length > 0 && (
              <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                <div className="px-4 py-3 bg-stone-50 border-b border-stone-200">
                  <h3 className="font-semibold text-charcoal text-sm flex items-center gap-2">
                    <ArrowDownCircle className="w-4 h-4 text-green-600" />Son Tahsilatlar
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="report-payments-table">
                    <thead>
                      <tr className="border-b border-stone-200 bg-stone-50/50">
                        <th className="px-4 py-2 text-left text-xs font-semibold text-charcoal/60 uppercase">Tarih</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-charcoal/60 uppercase">Musteri</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-charcoal/60 uppercase">Tutar</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-charcoal/60 uppercase">Yontem</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-charcoal/60 uppercase">Not</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rPayments.map((p, i) => (
                        <tr key={p.id || `payment-${i}`} className="border-b border-stone-100 hover:bg-green-50/30 transition-colors">
                          <td className="px-4 py-2.5 text-charcoal/60">{new Date(p.created_at).toLocaleDateString('tr-TR')}</td>
                          <td className="px-4 py-2.5 font-medium text-charcoal">{p.customer_name}</td>
                          <td className="px-4 py-2.5 text-right font-bold text-green-600">€{p.amount.toFixed(2)}</td>
                          <td className="px-4 py-2.5 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.payment_method === 'nakit' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                              {p.payment_method === 'nakit' ? 'Nakit' : 'Kart'}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-charcoal/50 text-xs">{p.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ========== CUSTOMER LIST VIEW ==========
  if (view === 'customers') {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-600/10 rounded-full flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <h2 className="font-playfair text-xl font-semibold text-charcoal" data-testid="veresiye-title">Veresiye Defteri</h2>
              <p className="text-sm text-charcoal/60">Musteri bazli veresiye takibi</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2 text-right">
              <p className="text-xs text-red-500/70">Toplam Bakiye</p>
              <p className="font-bold text-red-600 text-lg" data-testid="veresiye-total-balance">€{totalBalance.toFixed(2)}</p>
            </div>
            <Button onClick={openReport} variant="outline" className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="veresiye-report-btn">
              <BarChart3 className="w-4 h-4 mr-1" />Rapor
            </Button>
            <Button onClick={() => setShowNewEntry(true)} className="bg-amber-700 text-white hover:bg-amber-600 rounded-full" data-testid="veresiye-new-entry-btn">
              <Plus className="w-4 h-4 mr-1" />Yeni Veresiye
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-3 w-4 h-4 text-charcoal/40" />
          <Input placeholder="Musteri ara (isim veya telefon)..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-10" data-testid="veresiye-search" />
        </div>

        {/* Customer Cards */}
        {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-amber-600 border-t-transparent mx-auto" /></div>
        ) : filteredCustomers.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
            <BookOpen className="w-12 h-12 text-charcoal/20 mx-auto mb-3" />
            <p className="text-charcoal/50 mb-1">Veresiye kaydı bulunamadı</p>
            <p className="text-charcoal/40 text-sm">Yeni veresiye eklemek için butona tıklayın</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredCustomers.map((cust, idx) => {
              const initials = (cust.customer_name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
              const isOpen = (cust.balance || 0) > 0;
              return (
                <motion.button
                  key={idx}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03 }}
                  whileHover={{ y: -2 }}
                  onClick={() => openCustomerDetail(cust)}
                  className={`group relative bg-white rounded-2xl p-4 text-left shadow-sm hover:shadow-lg transition-all ring-1 ${isOpen ? 'ring-red-200 hover:ring-red-400' : 'ring-emerald-200 hover:ring-emerald-400'}`}
                  data-testid={`veresiye-customer-${idx}`}>
                  {/* Status accent bar */}
                  <span className={`absolute top-0 left-4 right-4 h-0.5 rounded-b-full ${isOpen ? 'bg-red-400' : 'bg-emerald-400'}`} />
                  <div className="flex items-start justify-between mb-3 mt-1">
                    <div className="flex items-center gap-3">
                      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center font-bold text-sm shadow-sm ${isOpen ? 'bg-gradient-to-br from-red-100 to-red-200 text-red-700' : 'bg-gradient-to-br from-emerald-100 to-emerald-200 text-emerald-700'}`}>
                        {initials || <User className="w-5 h-5" />}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-charcoal truncate">{cust.customer_name}</p>
                        {cust.customer_phone && <p className="text-xs text-charcoal/50 truncate">{cust.customer_phone}</p>}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`font-playfair text-xl font-bold ${isOpen ? 'text-red-600' : 'text-emerald-600'}`}>
                        €{(cust.balance || 0).toFixed(2)}
                      </p>
                      <p className="text-[10px] uppercase tracking-wider text-charcoal/40">{isOpen ? 'Borç' : 'Kapalı'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-charcoal/50 pt-2 border-t border-stone-100">
                    <span className="inline-flex items-center gap-1 bg-stone-50 px-2 py-0.5 rounded-full">
                      <BookOpen className="w-3 h-3" />{cust.entry_count} kayıt
                    </span>
                    {cust.open_count > 0 && (
                      <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">
                        {cust.open_count} açık
                      </span>
                    )}
                    <span className="ml-auto text-[10px]">{cust.last_date ? new Date(cust.last_date).toLocaleDateString('tr-TR') : ''}</span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}

        {/* New Entry Dialog */}
        <VeresiyeNewEntryDialog
          open={showNewEntry}
          onClose={() => { setShowNewEntry(false); setCart([]); setNewName(''); setNewPhone(''); setNewNotes(''); setDiscountPct('0'); }}
          newName={newName} setNewName={setNewName}
          newPhone={newPhone} setNewPhone={setNewPhone}
          newNotes={newNotes} setNewNotes={setNewNotes}
          cart={cart} cartTotal={cartTotal}
          discountPct={discountPct} setDiscountPct={setDiscountPct}
          discountValue={discountValue} finalTotal={finalTotal}
          addToCart={addToCart} updateCartQty={updateCartQty} removeFromCart={removeFromCart}
          menuSearch={menuSearch} setMenuSearch={setMenuSearch}
          menuTab={menuTab} setMenuTab={setMenuTab}
          filteredMenu={filteredMenu} menuCategories={menuCategories}
          filteredBev={filteredBev} bevCategories={bevCategories}
          cateringItems={cateringItems}
          loading={loading} onSubmit={handleCreateEntry}
        />
      </div>
    );
  }

  // ========== CUSTOMER DETAIL VIEW ==========
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <button onClick={goBack} className="p-2 rounded-full bg-stone-100 hover:bg-stone-200 transition-colors" data-testid="veresiye-back-btn">
          <ChevronLeft className="w-5 h-5 text-charcoal" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="font-playfair text-xl font-semibold text-charcoal">{selectedCustomer?.customer_name}</h2>
            {selectedCustomer?.customer_phone && (
              <span className="text-sm text-charcoal/50 flex items-center gap-1"><Phone className="w-3 h-3" />{selectedCustomer.customer_phone}</span>
            )}
          </div>
          <p className="text-sm text-charcoal/60">Veresiye detaylari</p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`rounded-xl px-4 py-2 text-right ${(selectedCustomer?.balance || 0) > 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
            <p className="text-xs text-charcoal/50">Kalan Borc</p>
            <p className={`font-bold text-lg ${(selectedCustomer?.balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`} data-testid="veresiye-customer-balance">
              €{(selectedCustomer?.balance || 0).toFixed(2)}
            </p>
          </div>
          <Button onClick={() => { setShowNewEntry(true); setNewName(selectedCustomer?.customer_name || ''); setNewPhone(selectedCustomer?.customer_phone || ''); }}
            className="bg-amber-700 text-white hover:bg-amber-600 rounded-full" data-testid="veresiye-add-entry-btn">
            <Plus className="w-4 h-4 mr-1" />Ekle
          </Button>
          <Button onClick={() => setShowStatement(true)} variant="outline" className="rounded-full border-amber-700 text-amber-700 hover:bg-amber-50" data-testid="veresiye-statement-btn">
            <FileText className="w-4 h-4 mr-1" />Ekstre
          </Button>
        </div>
      </div>

      {/* Date Filter */}
      <div className="flex flex-wrap items-center gap-2 bg-white rounded-xl p-3 border border-stone-200">
        <Calendar className="w-4 h-4 text-charcoal/40" />
        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-40 text-sm" data-testid="veresiye-date-from" />
        <span className="text-charcoal/40 text-sm">-</span>
        <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-40 text-sm" data-testid="veresiye-date-to" />
        <Button size="sm" variant="outline" onClick={() => fetchCustomerEntries(selectedCustomer.customer_name, selectedCustomer.customer_phone)}
          className="text-xs" data-testid="veresiye-filter-btn">Filtrele</Button>
        {(dateFrom || dateTo) && (
          <Button size="sm" variant="ghost" onClick={() => { setDateFrom(''); setDateTo(''); fetchCustomerEntries(selectedCustomer.customer_name, selectedCustomer.customer_phone); }}
            className="text-xs text-charcoal/50">Temizle</Button>
        )}
      </div>

      {/* Summary Bar */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl p-3 border border-stone-200 text-center">
          <p className="text-xs text-charcoal/50">Toplam Borc</p>
          <p className="font-bold text-charcoal" data-testid="veresiye-total-debt">€{(selectedCustomer?.total_amount || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-xl p-3 border border-stone-200 text-center">
          <p className="text-xs text-charcoal/50">Odenen</p>
          <p className="font-bold text-green-600" data-testid="veresiye-total-paid">€{(selectedCustomer?.paid_amount || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-xl p-3 border border-stone-200 text-center">
          <p className="text-xs text-charcoal/50">Kalan</p>
          <p className="font-bold text-red-600">€{(selectedCustomer?.balance || 0).toFixed(2)}</p>
        </div>
      </div>

      {/* Entries */}
      {loading ? (
        <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-4 border-amber-600 border-t-transparent mx-auto" /></div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-stone-200">
          <FileText className="w-10 h-10 text-charcoal/20 mx-auto mb-2" />
          <p className="text-charcoal/50">Bu musteriye ait veresiye kaydı bulunamadı</p>
        </div>
      ) : (
        <>
          {/* Bulk Selection Toolbar */}
          {openEntries.length > 0 && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="bulk-payment-toolbar">
              <span className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
                <Check className="w-4 h-4" />
                {selectedEntryIds.length} / {openEntries.length} açık kayıt seçili
              </span>
              {selectedEntryIds.length > 0 && (
                <span className="text-sm font-bold text-charcoal bg-white border border-amber-200 rounded-full px-3 py-0.5" data-testid="bulk-selected-total">
                  Toplam: €{selectedRemainingTotal.toFixed(2)}
                </span>
              )}
              <div className="ml-auto flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" onClick={selectAllOpenEntries}
                  className="text-xs rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="bulk-select-all-btn">
                  Tümünü Seç
                </Button>
                {selectedEntryIds.length > 0 && (
                  <Button size="sm" variant="ghost" onClick={clearSelection}
                    className="text-xs rounded-full text-charcoal/60" data-testid="bulk-clear-btn">
                    Temizle
                  </Button>
                )}
                <Button size="sm" onClick={openBulkPaymentDialog}
                  disabled={selectedEntryIds.length === 0}
                  className="bg-green-600 text-white hover:bg-green-500 text-xs rounded-full disabled:opacity-50"
                  data-testid="bulk-pay-btn">
                  <Wallet className="w-3 h-3 mr-1" />Toplu Ödeme Al
                </Button>
              </div>
            </div>
          )}

        <div className="space-y-3">
          {entries.map(entry => {
            const remaining = Math.max(0, entry.total_amount - entry.paid_amount);
            const isOpen = entry.status === 'open';
            const isSelected = selectedEntryIds.includes(entry.id);
            return (
              <div key={entry.id} className={`bg-white rounded-xl border overflow-hidden transition-all ${isSelected ? 'ring-2 ring-amber-400 border-amber-300' : isOpen ? 'border-amber-200' : 'border-green-200'}`}
                data-testid={`veresiye-entry-${entry.id}`}>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {isOpen && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleEntrySelection(entry.id)}
                          className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500 cursor-pointer accent-amber-600"
                          data-testid={`veresiye-entry-checkbox-${entry.id}`}
                          title="Toplu ödeme için seç"
                        />
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${isOpen ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                        {isOpen ? 'Acik' : 'Kapali'}
                      </span>
                      <span className="text-sm text-charcoal/50">
                        {new Date(entry.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </span>
                      <span className="text-xs text-charcoal/40">
                        {new Date(entry.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-charcoal">€{entry.total_amount.toFixed(2)}</span>
                      {isOpen && (
                        <Button size="sm" onClick={() => { setShowPayment(entry); setPayAmount(remaining.toFixed(2)); }}
                          className="bg-green-600 text-white hover:bg-green-500 text-xs rounded-full h-7 px-3"
                          data-testid={`veresiye-pay-${entry.id}`}>
                          <Wallet className="w-3 h-3 mr-1" />Ode
                        </Button>
                      )}
                      <button onClick={() => handleDelete(entry.id)} className="text-red-400 hover:text-red-600 p-1" data-testid={`veresiye-delete-${entry.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Items */}
                  <div className="space-y-1 mb-2">
                    {entry.items.map((item, i) => (
                      <div key={`${entry.id}-${item.name}-${i}`} className="flex justify-between text-sm">
                        <span className="text-charcoal/70">{item.quantity}x {item.name}</span>
                        <span className="text-charcoal/50">€{(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  {/* Subtotal + Discount breakdown (only when discount applied) */}
                  {(entry.discount_percent > 0 || entry.discount_amount > 0) && (
                    <div className="bg-amber-50/60 border border-amber-100 rounded-lg px-3 py-2 mb-2 space-y-0.5 text-xs">
                      <div className="flex justify-between text-charcoal/70">
                        <span>Ara Toplam</span>
                        <span>€{(entry.subtotal ?? entry.total_amount).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-red-600 font-medium">
                        <span>Indirim ({entry.discount_percent.toFixed(entry.discount_percent % 1 === 0 ? 0 : 1)}%)</span>
                        <span>-€{(entry.discount_amount ?? 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-charcoal pt-0.5 border-t border-amber-200/60">
                        <span>Genel Toplam</span>
                        <span>€{entry.total_amount.toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {entry.notes && <p className="text-xs text-charcoal/40 italic mb-2">Not: {entry.notes}</p>}

                  {/* Payment progress */}
                  {entry.paid_amount > 0 && (
                    <div className="mt-2 pt-2 border-t border-stone-100">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-green-600">Odenen: €{entry.paid_amount.toFixed(2)}</span>
                        {isOpen && <span className="text-red-500">Kalan: €{remaining.toFixed(2)}</span>}
                      </div>
                      <div className="w-full bg-stone-100 rounded-full h-1.5">
                        <div className="bg-green-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, (entry.paid_amount / entry.total_amount) * 100)}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {/* New Entry Dialog */}
      <VeresiyeNewEntryDialog
        open={showNewEntry}
        onClose={() => { setShowNewEntry(false); setCart([]); setNewNotes(''); setDiscountPct('0'); }}
        newName={newName} setNewName={setNewName}
        newPhone={newPhone} setNewPhone={setNewPhone}
        newNotes={newNotes} setNewNotes={setNewNotes}
        cart={cart} cartTotal={cartTotal}
        discountPct={discountPct} setDiscountPct={setDiscountPct}
        discountValue={discountValue} finalTotal={finalTotal}
        addToCart={addToCart} updateCartQty={updateCartQty} removeFromCart={removeFromCart}
        menuSearch={menuSearch} setMenuSearch={setMenuSearch}
        menuTab={menuTab} setMenuTab={setMenuTab}
        filteredMenu={filteredMenu} menuCategories={menuCategories}
        filteredBev={filteredBev} bevCategories={bevCategories}
        cateringItems={cateringItems}
        loading={loading} onSubmit={handleCreateEntry}
        prefillName={!!selectedCustomer}
      />

      {/* Customer Statement Dialog */}
      <VeresiyeCustomerStatement
        open={showStatement}
        onClose={() => setShowStatement(false)}
        customerName={selectedCustomer?.customer_name}
        customerPhone={selectedCustomer?.customer_phone}
        token={token}
      />

      {/* Payment Dialog */}
      <Dialog open={!!showPayment} onOpenChange={() => setShowPayment(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Odeme Kaydet</DialogTitle></DialogHeader>
          {showPayment && (
            <div className="space-y-4">
              <div className="bg-stone-50 rounded-xl p-3 text-sm">
                <div className="flex justify-between mb-1">
                  <span className="text-charcoal/60">Toplam Borc</span>
                  <span className="font-bold">€{showPayment.total_amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span className="text-charcoal/60">Odenen</span>
                  <span className="text-green-600">€{showPayment.paid_amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-stone-200">
                  <span className="font-semibold">Kalan</span>
                  <span className="font-bold text-red-600">€{(showPayment.total_amount - showPayment.paid_amount).toFixed(2)}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-charcoal/70 mb-1 block">Odeme Tutari (€)</label>
                <Input type="number" step="0.01" min="0.01" max={showPayment.total_amount - showPayment.paid_amount}
                  value={payAmount} onChange={e => setPayAmount(e.target.value)}
                  placeholder="0.00" className="text-lg font-bold" data-testid="veresiye-pay-amount" />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => setPayMethod('nakit')}
                  className={`flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl border-2 text-xs font-semibold transition-all ${payMethod === 'nakit' ? 'border-green-500 bg-green-50 text-green-700' : 'border-stone-200 text-charcoal/60'}`}
                  data-testid="veresiye-pay-cash"><Banknote className="w-4 h-4" />Nakit</button>
                <button onClick={() => setPayMethod('kart')}
                  className={`flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl border-2 text-xs font-semibold transition-all ${payMethod === 'kart' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-stone-200 text-charcoal/60'}`}
                  data-testid="veresiye-pay-card"><CreditCard className="w-4 h-4" />Kart</button>
                <button onClick={() => setPayMethod('havale')}
                  className={`flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl border-2 text-xs font-semibold transition-all ${payMethod === 'havale' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-stone-200 text-charcoal/60'}`}
                  data-testid="veresiye-pay-havale"><ArrowDownCircle className="w-4 h-4" />Havale</button>
              </div>

              <Input placeholder="Odeme notu (opsiyonel)" value={payNotes} onChange={e => setPayNotes(e.target.value)} data-testid="veresiye-pay-notes" />

              <Button onClick={handlePayment} disabled={loading} className="w-full bg-green-600 text-white hover:bg-green-500 py-5 font-semibold" data-testid="veresiye-pay-submit">
                {loading ? 'Kaydediliyor...' : <><Check className="w-5 h-5 mr-2" />Odemeyi Kaydet</>}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Payment Dialog */}
      <Dialog open={showBulkPayment} onOpenChange={(o) => { if (!o) setShowBulkPayment(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wallet className="w-5 h-5 text-green-600" />Toplu Ödeme Al</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-charcoal/70">Seçili Kayıt</span>
                <span className="font-bold" data-testid="bulk-dialog-count">{selectedEntryIds.length} adet</span>
              </div>
              <div className="flex justify-between pt-1 border-t border-amber-200">
                <span className="font-semibold">Toplam Kalan</span>
                <span className="font-bold text-red-600" data-testid="bulk-dialog-total">€{selectedRemainingTotal.toFixed(2)}</span>
              </div>
              <p className="text-[11px] text-charcoal/50 mt-1">Ödeme FIFO (eskiden yeniye) dağıtılır.</p>
            </div>

            <div>
              <label className="text-sm font-medium text-charcoal/70 mb-1 block">Ödeme Tutarı (€)</label>
              <Input type="number" step="0.01" min="0.01" max={selectedRemainingTotal}
                value={bulkPayAmount} onChange={e => setBulkPayAmount(e.target.value)}
                placeholder="0.00" className="text-lg font-bold" data-testid="bulk-pay-amount" />
            </div>

            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setBulkPayMethod('nakit')}
                className={`flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl border-2 text-xs font-semibold transition-all ${bulkPayMethod === 'nakit' ? 'border-green-500 bg-green-50 text-green-700' : 'border-stone-200 text-charcoal/60'}`}
                data-testid="bulk-pay-cash"><Banknote className="w-4 h-4" />Nakit</button>
              <button onClick={() => setBulkPayMethod('kart')}
                className={`flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl border-2 text-xs font-semibold transition-all ${bulkPayMethod === 'kart' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-stone-200 text-charcoal/60'}`}
                data-testid="bulk-pay-card"><CreditCard className="w-4 h-4" />Kart</button>
              <button onClick={() => setBulkPayMethod('havale')}
                className={`flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl border-2 text-xs font-semibold transition-all ${bulkPayMethod === 'havale' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-stone-200 text-charcoal/60'}`}
                data-testid="bulk-pay-havale"><ArrowDownCircle className="w-4 h-4" />Havale</button>
            </div>

            <Input placeholder="Ödeme notu (opsiyonel)" value={bulkPayNotes} onChange={e => setBulkPayNotes(e.target.value)} data-testid="bulk-pay-notes" />

            <Button onClick={handleBulkPayment} disabled={loading} className="w-full bg-green-600 text-white hover:bg-green-500 py-5 font-semibold" data-testid="bulk-pay-submit">
              {loading ? 'Kaydediliyor...' : <><Check className="w-5 h-5 mr-2" />Toplu Ödemeyi Kaydet</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default VeresiyeManagement;
